import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { ILearnerUsersState, initLearnerUsersState } from './types/learner-users.state';
import { computed, inject, Injectable } from '@angular/core';
import { LearnerService } from '@lb/api/mylb/services/learner.service';
import { take } from 'rxjs';
import { StorageService } from '@melodic-ui/services';
import { UserService } from '@lb/api/mylb/services/user.service';
import { LearnerApplicationStore } from './learner-application.store';
import { IUser } from '@lb/api/mylb/interfaces/iuser.interface';
import { MDAlertService } from '@melodic-ui/components';
import { ILearner } from '@lb/api/mylb/interfaces/ilearner.interface';

/**
 * The learner to show when nobody has been chosen yet.
 *
 * Not simply the first row: GetLearnersForUser orders by most recent enrollment start, so the head
 * of the list is often a deactivated user. GET /user/{id} answers 204 for an inactive user, which left
 * no active learner and every page waiting on one. Active users with an active enrollment come first,
 * then by name — the same order the switcher lists them in, so its checkmark and the loaded learner
 * agree.
 */
function pickDefaultLearner(learners: ILearner[]): ILearner | undefined {
	const rank = (l: ILearner) => (l.isActive ? 0 : 2) + (l.hasActiveEnrollment ? 0 : 1);

	return [...learners].sort((a, b) => rank(a) - rank(b) || a.learnerFullName.localeCompare(b.learnerFullName))[0];
}

function getActiveLearnerUser(user: IUser | null, initialLoad: boolean): IUser | undefined {
	if (!user) {
		// 204 from GET /user/{id} — the user is inactive or gone. Nothing to select.
		return undefined;
	}

	// Only set user if they are a learner (roleID === 1) or not in initial load
	return user.roleID === 1 ? user : initialLoad ? undefined : user;
}

export function learnerUsersState() {
	return signalStoreFeature(
		withState<ILearnerUsersState>(initLearnerUsersState),
		withMethods((state, userService = inject(UserService), alertService = inject(MDAlertService)) => ({
			loadActiveLearnerUser(userID: number, initialLoad: boolean) {
				patchState(state, { activeUserLoading: true, activeUserLoaded: false });
				userService
					.getUser(userID)
					.pipe(take(1))
					.subscribe({
						next: (user) => {
							// Never replace a learner that has already been chosen with "nobody".
							//
							// On the initial load this call races loadLearnerUsers(), which picks the first
							// reachable learner when none is set. getActiveLearnerUser deliberately answers
							// undefined for a non-learner so that choice is deferred to it — but if this response
							// lands second, the plain assignment wiped the selection loadLearnerUsers had just
							// made, and every page filtering on activeLearnerUserID stalls. Order of arrival
							// should not decide whether the app works.
							const resolved = getActiveLearnerUser(user, initialLoad);

							patchState(state, {
								activeLearnerUser: resolved ?? state.activeLearnerUser(),
								activeUserLoading: false,
								activeUserLoaded: true
							});
						},
						error: (error) => {
							console.error('Error fetching active learner user', error);
							patchState(state, { activeUserLoading: false });
						}
					});
			},
			updateActiveLearnerUser(activeLearnerUser: IUser, updateLearner: boolean) {
				patchState(state, { activeUserUpdating: true });

				userService
					.updateUser(activeLearnerUser)
					.pipe(take(1))
					.subscribe({
						next: (updatedUser) => {
							alertService.addAlert({
								title: 'Profile Updated Successfully!',
								type: 'success',
								message: 'This user profile has been successfully updated.',
								duration: 3000
							});

							if (updateLearner) {
								patchState(state, { activeLearnerUser: updatedUser, activeUserUpdating: false });
							} else {
								patchState(state, { activeUserUpdating: false });
							}
						},
						error: (error) => {
							alertService.addAlert({
								title: 'Profile Update Failed!',
								type: 'error',
								message: 'Something went wrong. Please try again.',
								duration: 3000
							});
							console.error('Error updating active learner user', error);
							patchState(state, { activeUserUpdating: false });
						}
					});
			},
			setActiveLearnerUser(activeLearnerUser: IUser | undefined) {
				patchState(state, { activeLearnerUser });
			}
		})),
		withMethods((state, learnerService = inject(LearnerService)) => ({
			loadLearnerUsers() {
				patchState(state, { learnersLoading: true, learnersLoaded: false });
				learnerService
					.getLearnersForUser()
					.pipe(take(1))
					.subscribe({
						next: (learnerUsers) => {
							const storageService = new StorageService(true);
							patchState(state, { learners: learnerUsers, learnersLoading: false, learnersLoaded: true });

							let activeLearnerUser = pickDefaultLearner(learnerUsers);
							if (storageService.hasKey('active-learner-user-id')) {
								const savedActiveLearnerUserID = storageService.get<number>('active-learner-user-id') as number;
								const savedUser = learnerUsers.find((learnerUser) => learnerUser.learnerUserID === savedActiveLearnerUserID);

								// A saved choice that has since been deactivated would stall the app the same way.
								if (savedUser?.isActive) {
									activeLearnerUser = savedUser;
								}
							}

							if (activeLearnerUser) {
								storageService.set('active-learner-user-id', activeLearnerUser.learnerUserID);
							}

							if (state.activeLearnerUser() === undefined && activeLearnerUser) {
								state.loadActiveLearnerUser(activeLearnerUser.learnerUserID, false);
							}
						},
						error: (error) => {
							console.error('Error fetching learners', error);
							patchState(state, { learnersLoading: false });
						}
					});
			}
		}))
	);
}

@Injectable()
export class LearnerUserStateService {
	private readonly learnerApplicationStore = inject(LearnerApplicationStore);

	public learners = this.learnerApplicationStore.learners;
	public learnersLoading = this.learnerApplicationStore.learnersLoading;
	public learnersLoaded = this.learnerApplicationStore.learnersLoaded;

	public activeLearnerUser = this.learnerApplicationStore.activeLearnerUser;
	public activeUserLoading = this.learnerApplicationStore.activeUserLoading;
	public activeUserLoaded = this.learnerApplicationStore.activeUserLoaded;
	public activeUserUpdating = this.learnerApplicationStore.activeUserUpdating;

	public activeLearnerUserID = computed(() => this.activeLearnerUser()?.userID);

	setActiveLearnerUser(activeLearnerUser: IUser | undefined): void {
		this.learnerApplicationStore.setActiveLearnerUser(activeLearnerUser);
	}

	public loadActiveLearnerUser(activeLearnerUserID: number, initialLoad: boolean): void {
		this.learnerApplicationStore.loadActiveLearnerUser(activeLearnerUserID, initialLoad);
	}

	public loadLearnerUsers(): void {
		this.learnerApplicationStore.loadLearnerUsers();
	}

	public updateActiveLearnerUser(activeLearnerUser: IUser | undefined, updateLearner: boolean): void {
		if (!activeLearnerUser) {
			return;
		}

		this.learnerApplicationStore.updateActiveLearnerUser(activeLearnerUser, updateLearner);
	}
}
